import React, { useState, useEffect } from 'react';
import {
    Paper,
    Typography,
    TextField,
    Switch,
    FormControlLabel,
    Button,
    Box,
    Alert,
    Snackbar
} from '@mui/material';
import axiosInstance from '../../../utils/axios';

const MenuButtonEditor = () => {
    const [settings, setSettings] = useState({
        menu_button: {
            enabled: false,
            text: 'Открыть меню',
            url: ''
        }
    });
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await axiosInstance.get('/bots-settings/get');
            if (response.data) {
                setSettings({
                    menu_button: response.data.menu_button || {
                        enabled: false,
                        text: 'Открыть меню',
                        url: ''
                    }
                });
            }
        } catch (error) {
            console.error('Ошибка при загрузке настроек:', error);
            setSnackbar({ open: true, message: 'Ошибка при загрузке настроек', severity: 'error' });
        }
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            await axiosInstance.put('/bots-settings/update', settings);
            setSnackbar({ open: true, message: 'Настройки успешно сохранены!', severity: 'success' });
        } catch (error) {
            console.error('Ошибка при сохранении:', error);
            setSnackbar({ open: true, message: 'Ошибка при сохранении настроек', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = (event) => {
        setSettings({
            ...settings,
            menu_button: {
                ...settings.menu_button,
                enabled: event.target.checked
            }
        });
    };

    const handleTextChange = (event) => {
        setSettings({
            ...settings,
            menu_button: {
                ...settings.menu_button,
                text: event.target.value
            }
        });
    };

    const handleUrlChange = (event) => {
        setSettings({
            ...settings,
            menu_button: {
                ...settings.menu_button,
                url: event.target.value
            }
        });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    return (
        <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 3, backgroundColor: '#f4f6f8' }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#333' }}>
                Настройка кнопки меню
            </Typography>

            <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
                Кнопка меню отображается слева от поля ввода сообщения в Telegram (обычно используется для Mini Apps или веб-приложений)
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                    <strong>Важно:</strong> После сохранения настроек необходимо перезапустить бота, чтобы изменения вступили в силу.
                    Кнопка будет открывать указанный URL в встроенном браузере Telegram.
                </Typography>
            </Alert>

            <Box sx={{ mb: 3 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={settings.menu_button.enabled}
                            onChange={handleToggle}
                            color="primary"
                        />
                    }
                    label={
                        <Typography sx={{ fontWeight: 'bold', color: '#00796b' }}>
                            {settings.menu_button.enabled ? 'Кнопка меню включена' : 'Кнопка меню отключена'}
                        </Typography>
                    }
                />
            </Box>

            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#333', mb: 1 }}>
                    Текст кнопки
                </Typography>
                <TextField
                    variant="outlined"
                    fullWidth
                    value={settings.menu_button.text}
                    onChange={handleTextChange}
                    disabled={!settings.menu_button.enabled}
                    placeholder="Открыть меню"
                    helperText="Этот текст будет отображаться на кнопке меню"
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: settings.menu_button.enabled ? '#fff' : '#e0e0e0',
                            '&:hover fieldset': {
                                borderColor: '#00796b',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#00796b',
                            },
                        },
                    }}
                />
            </Box>

            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#333', mb: 1 }}>
                    URL для открытия
                </Typography>
                <TextField
                    variant="outlined"
                    fullWidth
                    value={settings.menu_button.url}
                    onChange={handleUrlChange}
                    disabled={!settings.menu_button.enabled}
                    placeholder="https://example.com"
                    helperText="URL который будет открываться при нажатии на кнопку меню (должен начинаться с https://)"
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: settings.menu_button.enabled ? '#fff' : '#e0e0e0',
                            '&:hover fieldset': {
                                borderColor: '#00796b',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#00796b',
                            },
                        },
                    }}
                />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading}
                    sx={{
                        backgroundColor: '#00796b',
                        color: '#fff',
                        fontWeight: 'bold',
                        '&:hover': {
                            backgroundColor: '#004d40',
                        },
                        '&:disabled': {
                            backgroundColor: '#b0bec5',
                        },
                    }}
                >
                    {loading ? 'Сохранение...' : 'Сохранить'}
                </Button>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default MenuButtonEditor;
